// Tutor ProfilePage — account details (read-only) and profile details
// (editable). Wrapped in the tutor sidebar layout (TutorLayout).
import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch, patchJson } from "../../hooks/useApi.js";
import { useProfile } from "../../contexts/ProfileContext.js";
import StatCard from "../../components/shared/StatCard.js";
import Avatar from "../../components/shared/Avatar.js";
import { IconLock, IconCamera } from "../../components/shared/icons.js";
import { SPECIALISATION_OPTIONS } from "../../lib/profileFormOptions.js";
import { phoneSchema } from "@dm-lms/shared/validations";
import { phoneError } from "../../lib/phoneError.js";
import type { Specialisation } from "@dm-lms/shared/types";

interface TutorStats {
  totalCourses: number;
  totalStudents: number;
  sessionsRun: number;
  upcomingSessions: number;
}

const inputClass =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

export default function ProfilePage() {
  const { user } = useAuth();
  const { profile, isLoading, error, refetch } = useProfile<"tutor">();
  // Single aggregate call — see apps/api/src/routes/tutors/index.ts. Replaces
  // the old per-course roster fetch + client-side dedupe (N+1).
  const { data: stats } = useFetch<TutorStats>(user ? `/api/tutors/${user.id}/stats` : null);

  // ---- Card 2: profile details — seeded once from the first fetch. ----
  const seeded = useRef(false);
  const [phone, setPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [specialisation, setSpecialisation] = useState<Specialisation | "">("");
  const [qualifications, setQualifications] = useState("");
  const [bio, setBio] = useState("");
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile || seeded.current) return;
    seeded.current = true;
    setPhone(profile.phone ?? "");
    setSpecialisation(profile.specialisation ?? "");
    setQualifications(profile.qualifications ?? "");
    setBio(profile.bio ?? "");
  }, [profile]);

  function handlePhotoChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    // Local preview only — no upload wired up yet (R2 upload is a later task).
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setPhoneTouched(true);
    if (phoneError(phoneSchema, phone) !== null) return;
    setSaveError(null);
    setSaving(true);

    // A blank phone is omitted: the schema rejects "" and the server keeps the saved number.
    const trimmedPhone = phone.trim();
    const res = await patchJson("/api/profile/tutor", {
      ...(trimmedPhone !== "" ? { phone: trimmedPhone } : {}),
      ...(specialisation !== "" ? { specialisation } : {}),
      qualifications,
      bio,
    });

    setSaving(false);
    if (!res.ok) {
      setSaveError(res.error);
      return;
    }
    setSaved(true);
    refetch();
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Profile</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading profile…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && profile && (
        <div className="max-w-2xl space-y-6">
          {/* CARD 1 — Account (read-only) */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="mb-5 flex items-center gap-4">
              <Avatar name={profile.name} size="md" />
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium text-dm-navy">{profile.name}</p>
                  <IconLock width={14} height={14} className="text-dm-slate" />
                </div>
                <p className="text-xs text-dm-slate">Synced from Google</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-dm-navy">{profile.email}</p>
                <IconLock width={14} height={14} className="text-dm-slate" />
              </div>
              <p className="text-xs text-dm-slate">Synced from Google</p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Tutor code" value={profile.code} />
              <StatCard
                label="Member since"
                value={
                  user ? new Date(user.createdAt).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—"
                }
              />
              <StatCard label="Courses" value={stats?.totalCourses ?? "…"} />
              <StatCard label="Total students" value={stats?.totalStudents ?? "…"} />
              <StatCard label="Sessions run" value={stats?.sessionsRun ?? "…"} />
            </div>
          </div>

          {/* CARD 2 — Profile details (editable) */}
          <form onSubmit={saveProfile} className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-medium text-dm-navy">Profile details</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-dm-navy">
                  Phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  onBlur={() => setPhoneTouched(true)}
                  aria-invalid={phoneTouched && phoneError(phoneSchema, phone) !== null}
                  aria-describedby={phoneTouched && phoneError(phoneSchema, phone) !== null ? "phone-error" : undefined}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-dm-slate">Optional. Format: +254700000000</p>
                {phoneTouched && phoneError(phoneSchema, phone) !== null && (
                  <p id="phone-error" role="alert" className="mt-1 text-xs text-red-600">
                    {phoneError(phoneSchema, phone)}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="specialisation" className="block text-sm font-medium text-dm-navy">
                  Specialisation
                </label>
                <select
                  id="specialisation"
                  value={specialisation}
                  onChange={(event) => setSpecialisation(event.target.value as Specialisation | "")}
                  className={inputClass}
                >
                  <option value="">Select…</option>
                  {SPECIALISATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="qualifications" className="block text-sm font-medium text-dm-navy">
                  Qualifications
                </label>
                <textarea
                  id="qualifications"
                  rows={3}
                  value={qualifications}
                  onChange={(event) => setQualifications(event.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-dm-navy">
                  Bio
                </label>
                <textarea
                  id="bio"
                  rows={4}
                  maxLength={500}
                  value={bio}
                  onChange={(event) => setBio(event.target.value)}
                  className={inputClass}
                />
                <p className="mt-1 text-right text-xs text-dm-slate">{bio.length} / 500</p>
              </div>

              <div>
                <span className="mb-2 block text-sm font-medium text-dm-navy">Profile photo</span>
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile preview" className="mb-3 h-14 w-14 rounded-full object-cover" />
                ) : (
                  <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-dm-slate">
                    <IconCamera width={20} height={20} />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handlePhotoChange}
                  className="block w-full text-sm text-dm-slate file:mr-3 file:rounded-md file:border-0 file:bg-dm-navy file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-dm-blue"
                />
              </div>
            </div>

            {saveError && <p className="mt-3 text-sm text-red-600">{saveError}</p>}

            <div className="mt-4 flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              {saved && <span className="text-sm text-green-600">Saved</span>}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
